import React from 'react';
import { Scale, Atom, Zap, TrendingUp, Target, Sparkles, Link2, Eye, FlaskConical } from 'lucide-react';

const pillars = [
  {
    icon: Scale,
    number: '1',
    title: 'The Single Argument (J = S − C)',
    description: 'Standard physics contains many independent choices—operators, sectors, and phenomenological "shape functions." UET replaces that open-ended menu with a single variational principle: the realized description maximizes predictive success (S) while minimizing algorithmic cost (C). A small set of microphysical parameters may remain, but the key upgrade is that late-time cosmological behavior is derived from them rather than tuned by hand.'
  },
  {
    icon: Atom,
    number: '2',
    title: 'The Starobinsky Chassis',
    description: 'When you demand a gravity sector that is local, stable, and ghost-free (with luminal tensor propagation), the theory\'s admissible completions collapse to a rigid backbone: the GR-clean quadratic form f(R) = R + βR². This "chassis" provides a controlled spacetime container on which the rest of the framework operates, without introducing an unlimited landscape of modified-gravity knobs.'
  },
  {
    icon: Zap,
    number: '3',
    title: 'The Unified Dark Sector (Tri-Phase Φ⁶ Field)',
    description: 'UET introduces a single information-bearing dark field—a minimal sextic complex scalar—whose macroscopic behavior separates into three regimes: wave-supported (U-phase), collisionless/particle-like (X-phase), and dissipative/fluid-like (A-phase). These regimes provide one microphysical origin for multiple dark-sector phenomena, yielding dark-matter-like and dark-energy-like effects as different limits of the same underlying field dynamics.'
  },
  {
    icon: TrendingUp,
    number: '4',
    title: 'Dissipation as Cosmic Overhead (The A-Phase)',
    description: 'UET models a late-time dissipative regime of the dark sector (the A-phase) as the physical bookkeeping channel for irreversible processes in the dark field. In linear cosmology, its primary imprint is a causal drag on structure growth (not an added pressure term), while the background expansion remains compatible with a cosmological-constant–like acceleration. The result is a distinctive, testable late-time growth signature without introducing adjustable dark-energy dynamics.'
  },
  {
    icon: Link2,
    number: '5',
    title: 'The Regressive Selector Hierarchy (Two-Boundary Physics)',
    description: 'UET treats cosmology as a two-boundary problem: histories are constrained not only by an early boundary (the Big Bang side) but also by consistency requirements at a late boundary. The Regressive Selector Hierarchy formalizes how terminal constraints filter the space of admissible intermediate histories, yielding a time-symmetric selection structure without adding new local forces in the bulk.'
  },
  {
    icon: Sparkles,
    number: '6',
    title: 'The Genesis Theorem (Resolving the Void)',
    description: 'At the interpretive layer, UET frames "absolute nothingness" as dynamically unstable: a true void cannot enforce its own permanence because it contains no structure to specify or maintain that condition. In this reading, existence emerges as the self-consistent resolution of boundary closure—an internally closed causal account rather than an externally imposed start signal.'
  },
  {
    icon: Target,
    number: '7',
    title: 'Self-Causal Closure',
    description: 'UET\'s two-boundary structure allows a self-consistent loop: the late boundary constrains the class of admissible early conditions, and those early conditions evolve to realize the late boundary. In this sense, the universe can be described as a closed informational circuit—bootstrapped by global consistency—without requiring an external initiator within the formalism.'
  },
  {
    icon: Eye,
    number: '8',
    title: 'The Cybernetic Omega (G∞)',
    description: 'At the interpretive layer, UET points to a terminal "boundary object" that represents the limiting closure of the hierarchy—a final consistency condition for the global history. This is not introduced as theology, but as the conceptual endpoint suggested by taking the inverse-limit structure seriously: a limit state that functions as the final validator of the realized history.'
  },
  {
    icon: FlaskConical,
    number: '9',
    title: 'Rigorous Falsifiability (Kill Lines)',
    description: 'UET is built to be testable. The paper registers explicit falsifiers—"kill lines"—that must hold if the framework is correct. Examples include luminal gravitational-wave propagation (cₜ = 1) and the monotone-growth constraint (no linear-growth overshoot relative to \u039bCDM at fixed background). Upcoming large-scale surveys and multi-messenger measurements can therefore validate or decisively falsify the framework.'
  }
];

const ArchitectureSection = () => {
  return (
    <section style={{
      background: '#000000',
      padding: '100px 7.6923%',
      position: 'relative'
    }}>
      <div style={{
        maxWidth: '1400px',
        margin: '0 auto'
      }}>
        <h2 className="section-title" style={{
          fontSize: '48px',
          fontWeight: 600,
          lineHeight: 1.1,
          letterSpacing: '-0.02em',
          color: '#FFFFFF',
          marginBottom: '24px',
          textAlign: 'center'
        }}>
          THE ARCHITECTURE
        </h2>

        <div className="two-col-grid" style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '60px',
          alignItems: 'center',
          marginBottom: '60px'
        }}>
          <div>
            <h3 style={{
              fontSize: '32px',
              fontWeight: 600,
              lineHeight: 1.25,
              color: '#FFFFFF',
              marginBottom: '24px'
            }}>
              A Variational Principle Without Free Parameters
            </h3>
            
            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)',
              marginBottom: '20px'
            }}>
              At the foundation is an optimization functional that trades descriptive power against algorithmic cost. The universe maximizes how much information it can encode while minimizing the complexity required to process it.
            </p>

            <div style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.25)',
              padding: '24px',
              marginBottom: '20px'
            }}>
              <p style={{
                fontSize: '32px',
                fontWeight: 600,
                color: '#00FFD1',
                textAlign: 'center',
                fontFamily: 'monospace'
              }}>
                J = S - C
              </p>
            </div>

            <p style={{
              fontSize: '18px',
              fontWeight: 400,
              lineHeight: 1.5,
              color: 'rgba(255, 255, 255, 0.85)'
            }}>
              This isn't metaphor. From this principle, we derive f(R) gravity with a unique form—the Starobinsky chassis with R² corrections. No fine-tuning. No arbitrary constants. The structure emerges because it's the only ghost-free, local solution that satisfies the constraint.
            </p>
          </div>

          <div className="section-image" style={{
            position: 'relative',
            height: '400px',
            borderRadius: '8px',
            overflow: 'hidden',
            border: '1px solid rgba(255, 255, 255, 0.25)'
          }}>
            <img 
              src="https://customer-assets.emergentagent.com/job_omega-theory/artifacts/pn5w7g94_image%202%20THE%20ARCHITECTURE.png" 
              alt="Cosmic Balance"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                opacity: 0.9
              }}
            />
          </div>
        </div>

        <h3 className="pillars-title" style={{
          fontSize: '32px',
          fontWeight: 600,
          lineHeight: 1.25,
          color: '#FFFFFF',
          marginBottom: '40px',
          textAlign: 'center'
        }}>
          THE 9 CORE PILLARS
        </h3>

        <div className="pillars-grid" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '40px'
        }}>
          {pillars.map((pillar, index) => {
            const IconComponent = pillar.icon;
            return (
              <div key={index} style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
                padding: '32px',
                transition: 'all 0.4s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(0, 255, 209, 0.1)';
                e.currentTarget.style.borderColor = '#00FFD1';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: '#00FFD1',
                    color: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    fontWeight: 700
                  }}>
                    {pillar.number}
                  </div>
                  <IconComponent size={32} color="#00FFD1" />
                </div>
                <h4 style={{
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#FFFFFF',
                  marginBottom: '16px'
                }}>
                  {pillar.title}
                </h4>
                <p style={{
                  fontSize: '15px',
                  fontWeight: 400,
                  lineHeight: 1.5,
                  color: 'rgba(255, 255, 255, 0.85)'
                }}>
                  {pillar.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          section {
            padding: 60px 20px !important;
          }
          .section-title {
            font-size: 36px !important;
          }
          .two-col-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          .section-image {
            height: 300px !important;
          }
          .pillars-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 30px !important;
          }
        }
        @media (max-width: 768px) {
          section {
            padding: 40px 20px !important;
          }
          .section-title {
            font-size: 32px !important;
          }
          .pillars-title {
            font-size: 28px !important;
          }
          .pillars-grid {
            grid-template-columns: 1fr !important;
            gap: 20px !important;
          }
          .section-image {
            height: 250px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default ArchitectureSection;